
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import {
  convertRequestErrorToMap,
  isNotNullish,
  notEmpty,
  Nullable,
  useResource,
  useToast,
} from "@tager/admin-services";
import {
  createTabErrorFinder,
  FormFooter,
  SeoChangeEvent,
  SeoFieldGroup,
  TabType,
  TagerFormSubmitEvent,
  FormFieldFileInput,
  FormFieldUrlAliasInput,
  FormFieldRichTextInput,
  FormField,
} from "@tager/admin-ui";
import { Page } from "@tager/admin-layout";

import { FullSpecialType } from "@/typings/model";
import { createSpecial, getSpecial, updateSpecial } from "@/services/requests";
import {
  convertFormValuesToSpecialCreationPayload,
  convertFormValuesToSpecialUpdatePayload,
  convertSpecialToFormValues,
  FormValues,
} from "@/views/SpecialForm/SpecialForm.helpers";
import { getSpecialFormUrl, getSpecialListUrl } from "@/utils/paths";
import { getWebsiteOrigin } from "@/utils/common";

export default defineComponent({
  name: "SpecialForm",
  components: {
    Page,
    FormFooter,
    SeoFieldGroup,
    FormFieldFileInput,
    FormFieldUrlAliasInput,
    FormFieldRichTextInput,
    FormField,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();

    const specialId = computed<string>(() => route.params.specialId as string);

    const isCreation = computed<boolean>(() => specialId.value === "create");

    const [fetchSpecial, { data: special, loading: isSpecialLoading }] =
      useResource<Nullable<FullSpecialType>>({
        fetchResource: () => getSpecial(specialId.value),
        initialValue: null,
        resourceName: "special",
      });

    onMounted(() => {
      if (isCreation.value) return;

      fetchSpecial();
    });

    const values = ref<FormValues>(convertSpecialToFormValues(special.value));

    const errors = ref<Record<string, string>>({});
    const isSubmitting = ref<boolean>(false);

    watch(special, () => {
      values.value = convertSpecialToFormValues(special.value);
    });

    function submitForm(event: TagerFormSubmitEvent) {
      isSubmitting.value = true;

      const creationBody = convertFormValuesToSpecialCreationPayload(
        values.value
      );
      const updateBody = convertFormValuesToSpecialUpdatePayload(values.value);
      const requestPromise = isCreation.value
        ? createSpecial(creationBody)
        : updateSpecial(specialId.value, updateBody);

      requestPromise
        .then((response) => {
          errors.value = {};

          if (event.type === "create") {
            router.push(getSpecialFormUrl({ specialId: response.data.id }));
          }
          if (event.type === "create_exit" || event.type === "save_exit") {
            router.push(getSpecialListUrl());
          }
          if (event.type === "create_create-another") {
            values.value = convertSpecialToFormValues(null);
          }

          toast.show({
            variant: "success",
            title: "Success",
            body: isCreation.value
              ? `Спецпредложение успешно создана`
              : "Спецпредложение успешно обновлена",
          });
        })
        .catch((error) => {
          console.error(error);
          errors.value = convertRequestErrorToMap(error);
          toast.show({
            variant: "danger",
            title: "Ошибка",
            body: isCreation.value
              ? `Ошибка при создании спецпредложения`
              : "Ошибка при обновлении спецпредложения",
          });
        })
        .finally(() => {
          isSubmitting.value = false;
        });
    }

    const isContentLoading = computed<boolean>(() => isSpecialLoading.value);

    /** Tabs */

    const tabList = computed<Array<TabType>>(() => {
      const hasErrors = createTabErrorFinder(errors.value);

      return [
        {
          id: "common",
          label: "Основная",
          hasErrors: hasErrors([
            "name",
            "image",
            "brand",
            "content",
            "urlAlias",
          ]),
        },
        {
          id: "seo",
          label: "SEO",
          hasErrors: hasErrors([
            "pageTitle",
            "pageDescription",
            "openGraphImage",
          ]),
        },
      ].filter(notEmpty);
    });

    const selectedTabId = ref<string>(tabList.value[0].id);

    const pageTitle = computed<string>(() => {
      return isCreation.value
        ? `Добавить спецпредложение`
        : `Редактировать спецпредложение "${values.value.name}"`;
    });

    const pagePath = computed<string>(() => {
      return getWebsiteOrigin() + "/offers/";
    });

    const headerButtonList = computed<
      Array<{ text: string; href: string; target?: string }>
    >(() =>
      [
        special.value
          ? {
              text: "Посмотреть на сайте",
              href: getWebsiteOrigin() + "/offers/" + special.value.urlAlias,
              target: "_blank",
            }
          : null,
      ].filter(isNotNullish)
    );

    function handleSearchEngineOptimization({
      title,
      description,
      image,
    }: SeoChangeEvent) {
      values.value.pageTitle = title;
      values.value.pageDescription = description;
      values.value.openGraphImage = image;
    }

    return {
      backButtonUrl: getSpecialListUrl(),
      values,
      errors,
      isSubmitting,
      submitForm,
      isContentLoading,
      special,
      isCreation,
      pageTitle,
      tabList,
      selectedTabId,
      pagePath,
      headerButtonList,

      handleSearchEngineOptimization,
    };
  },
});
